import { User } from '@app/models/_index';
import { environment } from '@environments/environment';
import { getSubscriptionIds } from './subscription.utilities';

/**
 * Return boolean to determine if user has a matching subscription within another set of related subscriptions
 * This is needed by course specific pipes to determine if a course subscription matches or is related to a given
 * user's active subscriptions.
 * @param relatedSubscriptionIds - related subscription ids that a course can appear in. Some courses can be in 1 to N subscriptions
 * @param subscriptionId  - current course subscription id used to check if the user has this subscription for this course
 * @param user - current user logged in. User contains 1 to N active subscriptions that is registered to that given user
 * @returns - boolean indicating a user has a valid subscription
 */
export function isValidSubscription(
  relatedSubscriptionIds: number[],
  subscriptionId: number,
  user: User
): boolean {
  // Check if user has active subscription id that matches incoming subscription id
  // NOTE: Check if user has active subscription ids (it can match 1 or N subscription) that matches incoming subscription id, example
  // is Predictive Engagement: Altocloud - eLearning, which is in available in 3 subscriptions.
  // Workforce Management 8.5 Operation, is another rare exception where it is in 2 subscriptions.
  let isSubscribed = false;
  let activeSubscriptionIds: number[] = [];

  if (user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
    // Build list of active subscription ids form subscription objects
    activeSubscriptionIds = getSubscriptionIds(user.activeSubscriptions);

    if (relatedSubscriptionIds && relatedSubscriptionIds.length > 0) {
      let copyRelatedSubscriptionIds = relatedSubscriptionIds.slice();

      // NOTE: Remove invalid subscriptions from related subscription ids
      if (copyRelatedSubscriptionIds.length === 1) {
        // NOTE: If related subscription array is just 1 value, then you can just check
        // the user active subscription id against the course id
        const matchingSubscription =
          subscriptionId &&
          user.activeSubscriptions.find(
            (subscription) =>
              (subscription.id === subscriptionId ||
                (subscription.id == 593 && subscriptionId == 84)) && // special case. Premier should follow standard genesys cloud sub
              !isDigitalSubscription(subscription.id)
          );
        isSubscribed = !!matchingSubscription;
      } else {
        // NOTE: Check if the user's primary subscription matches the course subscription id and also
        // the related subscription ids. If either one is false, then the user is not subscribed.
        if (user && user.activeSubscriptions && user.activeSubscriptions.length === 1) {
          const userPrimarySubscriptionId = user.activeSubscriptions[0].id;
          isSubscribed =
            copyRelatedSubscriptionIds.includes(userPrimarySubscriptionId) &&
            !isDigitalSubscription(userPrimarySubscriptionId);
        } else {
          // NOTE:
          // CASE 1: Else handle rare case were course can be in more than 2 subscriptions and the
          // user's active scription doesn't match
          // CASE 2: User has multiple subscriptions and determine if the user subscription set (array) is
          // a subset of the multiple subscriptions.
          // CASE 3: Remove out Digital Genesys Cloud subscriptions, since they are not allowed to take courses in web.
          isSubscribed =
            copyRelatedSubscriptionIds.length > 1 &&
            copyRelatedSubscriptionIds.every(
              (val) =>
                copyRelatedSubscriptionIds.includes(val) &&
                activeSubscriptionIds.filter(
                  (userSubscriptionId) =>
                    userSubscriptionId === val && !isDigitalSubscription(userSubscriptionId)
                ).length <=
                  copyRelatedSubscriptionIds.filter(
                    (relatedSubcriptionId) =>
                      relatedSubcriptionId === val && !isDigitalSubscription(relatedSubcriptionId)
                  ).length
            );
          // Check again if isSubscribed with updated merged related subscription ids to see if the subscription is valid
          isSubscribed = hasSubscription(user, subscriptionId, copyRelatedSubscriptionIds);
        }
      }
    } else {
      const matchingSubscription =
        subscriptionId &&
        user.activeSubscriptions.find(
          (subscription) =>
            (subscription.id === subscriptionId ||
              (subscription.id == 593 && subscriptionId == 84)) && // special case. Premier should follow standard genesys cloud sub
            !isDigitalSubscription(subscription.id)
        );

      isSubscribed = !!matchingSubscription;
    }
  }

  return isSubscribed;
}

/**
 * Return boolean if subscription is found in a user's active subscription.
 * @param user - signed in user that contains active subscriptions (0 to N subscriptions)
 * @param subscriptionId - subscription id
 * @param relatedSubscriptionIds - related subscription ids
 * @returns - boolean if subscription is included in a user's active subscription
 */
export function hasSubscription(
  user: User,
  subscriptionId: number,
  relatedSubscriptionIds: number[]
): boolean {
  let isSubscribed = false;
  if (user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
    // NOTE: Do not allow any of the Genesys Cloud Digital subscriptions to access any Genesys Cloud courses
    // in beyond. They only can redirect to Docebo directly in Dashboard
    // Genesys Cloud Partner Digital Learning Subscription
    // Sandbox 522 - Genesys Cloud Partner Digital Plus
    // production 553 - Genesys Cloud Partner Digital Learning Subscription
    // Productou554 - Genesys Cloud Partner Digital Plus
    const matchingSubscription =
      subscriptionId &&
      user.activeSubscriptions.find(
        (subscription) =>
          (subscription.id === subscriptionId && !isDigitalSubscription(subscription.id)) ||
          isTrialSubscription(subscription.id, subscriptionId, relatedSubscriptionIds) ||
          isPartnerPremierSubscription(subscription.id, subscriptionId, relatedSubscriptionIds)
      );
    isSubscribed = !!matchingSubscription;
  }
  return isSubscribed;
}

/**
 * Return the user's primary subscription if the subscription matches or is included in related (multiple) subscriptions.
 * @param user - If user is signed in, then the transformed type will return a different display type.
 * @param subscriptionId - Subscription id used to check if the user has this subscription
 * @param relatedSubscriptionIds - Related subscriptions used to check if the course can appear in 1 to N subscriptions
 * @returns - primary user subscription id number
 */
export function getPrimaryUserSubscriptionId(
  user: User,
  subscriptionId: number,
  relatedSubscriptionIds: number[]
): number {
  // Default primary user id to subscription id if user has no subscriptions
  let primaryUserSubscriptionId = subscriptionId; // default course subscription id
  let activeSubscriptionIds: number[] = [];
  // User has multiple subscriptions. User filter to find the matching subscription or intersection.
  if (subscriptionId && user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
    activeSubscriptionIds = getSubscriptionIds(user.activeSubscriptions);

    // NOTE: Intersect two arrays to find the subscription ids that match. This returns an array with the intersected
    // or matching subscriptions.
    const matchingPrimarySubscription =
      relatedSubscriptionIds &&
      activeSubscriptionIds.length > 0 &&
      activeSubscriptionIds.filter(
        (userSubscriptionId: number) =>
          relatedSubscriptionIds.length > 0 &&
          relatedSubscriptionIds.includes(userSubscriptionId) &&
          !isDigitalSubscription(userSubscriptionId)
      );

    if (matchingPrimarySubscription && matchingPrimarySubscription.length > 0) {
      primaryUserSubscriptionId = matchingPrimarySubscription[0];
    }
  }
  return primaryUserSubscriptionId;
}

/**
 * Return boolean to determine if user has a Enable Enroll option for Certification Exam Genesys Cloud CX: Professional Certification and
 * Genesys Cloud CX: Developer Certification if the user is enrolled to Genesys Cloud CX Partner Digital Learning or Genesys Cloud CX Partner Digital Plus Learning
 * @param courseId - course Id to verify if it is Certification Exam Genesys Cloud CX: Professional Certification and Genesys Cloud CX: Developer Certification
 * @param user - current user logged in. User contains 1 to N active subscriptions that is registered to that given user
 * @returns - boolean indicating a user has a valid subscription
 */
export function isDigitalSubscriptionEnrolled(courseId: number, user: User): boolean {
  let isSubscribed = false;
  if (
    courseId == environment.beyond.genesys_cloud_cx_developer_certification_exam_id ||
    courseId == environment.beyond.genesys_cloud_cx_professional_certification_exam_id
  ) {
    const isDigitalSubscriptionEnrolled = user.enrollments.filter(
      (enrollment) =>
        enrollment.id == environment.beyond.genesys_cloud_partner_digital.subscription_id ||
        enrollment.id == environment.beyond.genesys_cloud_partner_digital_plus.subscription_id
    );
    if (isDigitalSubscriptionEnrolled.length > 0) {
      isSubscribed = true;
    }
  }
  return isSubscribed;
}

/**
 * Return boolean if user subscription is a Genesys Cloud Partner Digital Subscription
 * (either Digital or Digital Plus)
 * @param userSubscriptionId - user subscription id
 * @returns boolean
 */
export function isDigitalSubscription(userSubscriptionId: number): boolean {
  return (
    (userSubscriptionId &&
      userSubscriptionId === environment.beyond.genesys_cloud_partner_digital.subscription_id) ||
    userSubscriptionId === environment.beyond.genesys_cloud_partner_digital_plus.subscription_id
  );
}

/**
 * Return boolean if user subscription is a Genesys Cloud Trial Learning Subscription.
 * NOTE: Genesys Cloud Trial contains a subset of Genesys Cloud Courses, so not all Genesys Cloud courses
 * will be enabled. Only 23 known Genesys Cloud courses.
 * @param userSubscriptionId - user subscription id
 * @param courseSubscriptionId - course subscription id
 * @param relatedSubscriptionIds - related subscription ids
 * @returns
 */
export function isTrialSubscription(
  userSubscriptionId: number,
  courseSubscriptionId: number,
  relatedSubscriptionIds: number[]
): boolean {
  let isTrialSubscription = false;
  if (
    userSubscriptionId &&
    courseSubscriptionId &&
    relatedSubscriptionIds &&
    relatedSubscriptionIds.length > 0
  ) {
    isTrialSubscription =
      userSubscriptionId === environment.beyond.genesys_cloud_trial.subscription_id &&
      courseSubscriptionId === environment.beyond.genesys_cloud.subscription_id &&
      relatedSubscriptionIds.includes(environment.beyond.genesys_cloud_trial.subscription_id);
  }

  return isTrialSubscription;
}

/**
 * Return boolean if user subscription is a Genesys Cloud Partner Premier Learning Subscription.
 * NOTE: Genesys Cloud Premier Partner Premier contains a complete copy of the Genesys Cloud Learning Subscription
 * @param userSubscriptionId - user subscription id
 * @param courseSubscriptionId - course subscription id
 * @param relatedSubscriptionIds - related subscription ids
 * @returns
 */
export function isPartnerPremierSubscription(
  userSubscriptionId: number,
  courseSubscriptionId: number,
  relatedSubscriptionIds: number[]
): boolean {
  let isPartnerPremierSubscription = false;
  if (
    userSubscriptionId &&
    courseSubscriptionId &&
    relatedSubscriptionIds &&
    relatedSubscriptionIds.length > 0
  ) {
    isPartnerPremierSubscription =
      userSubscriptionId === environment.beyond.genesys_cloud_partner_premier.subscription_id &&
      courseSubscriptionId === environment.beyond.genesys_cloud.subscription_id &&
      relatedSubscriptionIds.includes(
        environment.beyond.genesys_cloud_partner_premier.subscription_id
      );
  }

  return isPartnerPremierSubscription;
}
